import { useMemo, useEffect, useRef, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import styled, { css } from 'styled-components';

import BuilderThemeProvider, {
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import { useBuilderContext } from 'hb-builder/hooks';
import { BUILDER_DOCUMENT_CHILDREN_TYPE } from 'hb-react/backoffice/types/builder/builderDocument';
import mergeRefs from 'hb-react/shared/utils/mergeRefs';

import { handleTopOrBottomCompo } from './utils';

export const PLACEHOLDER_HEIGHT = {
  section: 8,
  single: 16,
};

const usePlaceholderPosition = ({
  isOver,
  item,
  overItem,
  isSingleComponent,
  ref,
}) => {
  const [placeholderPosition, setPlaceholderPosition] = useState(null);
  const { setOverSectionDirection, getSpecificParentByType } =
    useBuilderContext();

  useEffect(() => {
    if (isOver) {
      let requestId;

      const handleDrag = (event) => {
        requestId = window.requestAnimationFrame(() => {
          if (event.clientY && ref.current) {
            const {
              height: componentHeight,
              bottom: componentBottom,
              top: componentTop,
            } = ref.current.getBoundingClientRect();

            const isCompoBottom = event.clientY > componentBottom - 6;
            const isCompoTop = event.clientY < componentTop + 6;

            const isTopOrBottomSingleCompo =
              isSingleComponent && (isCompoTop || isCompoBottom);

            if (isTopOrBottomSingleCompo) {
              const parentSection = getSpecificParentByType(
                overItem.id,
                BUILDER_DOCUMENT_CHILDREN_TYPE.SECTION,
              );
              const parentColumn = getSpecificParentByType(
                overItem.id,
                BUILDER_DOCUMENT_CHILDREN_TYPE.COLUMN,
              );

              handleTopOrBottomCompo({
                isCompoBottom,
                isCompoTop,
                item,
                overItem,
                parentSection,
                parentColumn,
                setOverSectionDirection,
              });
            } else {
              setOverSectionDirection(null);
            }
            const position =
              event.clientY >= componentTop + componentHeight / 2
                ? 'bottom'
                : 'top';

            setPlaceholderPosition((prevState) =>
              prevState === position ? prevState : position,
            );
          }
        });
      };

      document.addEventListener('dragover', handleDrag);

      return () => {
        window.cancelAnimationFrame(requestId);
        document.removeEventListener('dragover', handleDrag);
      };
    }
    setOverSectionDirection(null);
  }, [
    getSpecificParentByType,
    isOver,
    isSingleComponent,
    item,
    overItem,
    ref,
    setOverSectionDirection,
  ]);

  return placeholderPosition;
};

const Placeholder = ({
  canDrop,
  children,
  dropRef,
  isOver,
  item,
  overItem,
  overType,
  placeholderPositions,
}) => {
  const oufOfBoxRef = useRef();
  const wrapperRef = useRef();

  const isSingleComponent = overType === 'single';
  const placeholderPosition = usePlaceholderPosition({
    isOver,
    isSingleComponent,
    item,
    overItem,
    ref: wrapperRef,
  });

  const validDrop = useMemo(
    () => canDrop(placeholderPosition),
    [canDrop, placeholderPosition],
  );

  const displayBottom = useMemo(
    () =>
      validDrop &&
      placeholderPositions.includes('bottom') &&
      placeholderPosition === 'bottom',
    [validDrop, placeholderPosition, placeholderPositions],
  );

  const displayTop = useMemo(
    () =>
      validDrop &&
      placeholderPositions.includes('top') &&
      placeholderPosition === 'top',
    [validDrop, placeholderPosition, placeholderPositions],
  );

  const displayPlaceholder = displayBottom || displayTop;

  const springWrapper = useSpring({
    transform: displayBottom ? 'translateY(-4px)' : 'translateY(4px)',
  });

  const placeholderHeight = isSingleComponent
    ? PLACEHOLDER_HEIGHT.single
    : PLACEHOLDER_HEIGHT.section;

  const getPlaceHolderStyle = (display) => ({
    from: { height: '0px' },
    to: {
      height: !display ? '0px' : `${placeholderHeight}px`,
    },
  });

  const placeholderTopStyle = useSpring(getPlaceHolderStyle(displayTop));
  const placeholderBottomStyle = useSpring(getPlaceHolderStyle(displayBottom));

  return (
    <BuilderThemeProvider viewAs={VIEW_AS.ADMIN}>
      <div
        ref={mergeRefs([wrapperRef, dropRef])}
        style={{
          width: '100%',
          height:
            isOver &&
            overItem.category === 'single' &&
            oufOfBoxRef.current?.offsetHeight
              ? // Small trick to by pass the weird behavior of React DnD.
                // If no height, the drag doesn't work
                `${
                  oufOfBoxRef.current.offsetHeight +
                  (displayTop || displayBottom ? placeholderHeight : 0)
                }px`
              : undefined,
        }}
      >
        <div>
          {displayTop && <PlaceholderStyled style={placeholderTopStyle} />}
          <Wrapper
            ref={oufOfBoxRef}
            style={{ ...(displayPlaceholder && springWrapper) }}
            $height={displayPlaceholder ? '100%' : ''}
          >
            <BuilderThemeProvider viewAs={VIEW_AS.USER}>
              {children}
            </BuilderThemeProvider>
          </Wrapper>
          {displayBottom && (
            <PlaceholderStyled style={placeholderBottomStyle} />
          )}
        </div>
      </div>
    </BuilderThemeProvider>
  );
};

export default Placeholder;

const PlaceholderStyled = styled(animated.div)`
  ${({ theme }) => css`
    background-color: ${theme.colors.primary};
    border-radius: 2px;
    overflow: hidden;
    pointer-events: none;
  `}
`;

const Wrapper = styled(animated.div)`
  ${({ $height }) => `
    cursor: auto;
    display: flex;
    flex-direction: column;
    min-height: inherit;
    pointer-events: all;
    height: ${$height};
  `}
`;
